import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Building, Users, UserPlus, Edit, Trash2,
  Eye, Mail, Phone, MapPin, Play, Pause, Key, Download
} from 'lucide-react';
import { STATUS_CONFIG } from './constants';
import { useAuth } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getStatusBadge(status) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.actif;
  const Icon = config.icon;
  return <Badge className={`${config.color} text-white`}><Icon className="h-3 w-3 mr-1" />{config.label}</Badge>;
}

export default function CoopersTab({
  filteredCoopers, setShowCreateCooperModal, setSelectedItem, setShowDetailModal,
  openEditCooper, updateCooperStatus, validateCooper, deleteCooper, openResetPasswordModal
}) {
  const { getAuthHeader } = useAuth();

  const handleExportFiche = async (cooper) => {
    const exportId = cooper.id || cooper.user_id;
    if (!exportId) {
      toast.error('Identifiant COOPER\'S manquant');
      return;
    }
    const toastId = toast.loading('Génération de l\'export en cours...');
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/coopers/${exportId}/export`,
        { headers: getAuthHeader(), responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeName = `${cooper.prenom || ''}_${cooper.nom || ''}`.replace(/\s+/g, '_').replace(/[^\w.-]/g, '') || 'cooper';
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      link.href = url;
      link.download = `cooper_${safeName}_${ts}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export téléchargé', { id: toastId });
    } catch (err) {
      const msg = err?.response?.status === 404
        ? 'COOPER\'S introuvable'
        : 'Erreur lors de la génération de l\'export';
      toast.error(msg, { id: toastId });
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filteredCoopers.map((cooper) => (
          <Card key={cooper.id} className="hover:shadow-lg transition-all" data-testid={`cooper-card-${cooper.id}`}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center text-lg font-bold text-purple-700">
                    {(cooper.prenom?.[0] || '') + (cooper.nom?.[0] || '')}
                  </div>
                  <div>
                    <CardTitle className="text-base">{cooper.prenom} {cooper.nom}</CardTitle>
                    <p className="text-sm text-gray-500">{cooper.entreprise || 'Sans entreprise'}</p>
                  </div>
                </div>
                {getStatusBadge(cooper.statut || (cooper.is_validated ? 'actif' : 'en_attente'))}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-600"><Mail className="h-4 w-4" /><span className="truncate">{cooper.email}</span></div>
                {cooper.telephone && <div className="flex items-center gap-2 text-gray-600"><Phone className="h-4 w-4" /><span>{cooper.telephone}</span></div>}
                {cooper.ville && <div className="flex items-center gap-2 text-gray-600"><MapPin className="h-4 w-4" /><span>{cooper.ville}</span></div>}
                <div className="flex items-center gap-2 text-gray-600"><Users className="h-4 w-4" /><span>{cooper.membres_count || 0} membres</span></div>
              </div>
              <div className="flex flex-wrap gap-1 mt-4 pt-3 border-t">
                <Button size="sm" variant="ghost" onClick={() => { setSelectedItem(cooper); setShowDetailModal(true); }}><Eye className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => openEditCooper(cooper)}><Edit className="h-4 w-4" /></Button>
                {cooper.statut === 'suspendu' || (!cooper.statut && !cooper.is_validated) ? (
                  <Button size="sm" variant="ghost" className="text-green-600" onClick={() => !cooper.is_validated ? validateCooper(cooper.id) : updateCooperStatus(cooper.id, 'actif')} title={!cooper.is_validated ? "Valider" : "Activer"}>
                    <Play className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button size="sm" variant="ghost" className="text-orange-600" onClick={() => updateCooperStatus(cooper.id, 'suspendu')} title="Suspendre"><Pause className="h-4 w-4" /></Button>
                )}
                <Button size="sm" variant="ghost" className="text-blue-600" onClick={() => openResetPasswordModal(cooper, 'cooper')} title="Réinitialiser le mot de passe"><Key className="h-4 w-4" /></Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                  onClick={() => handleExportFiche(cooper)}
                  title="Exporter la fiche + documents (ZIP)"
                  data-testid={`cooper-export-${cooper.id}`}
                >
                  <Download className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteCooper(cooper.id)} title="Supprimer"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {filteredCoopers.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Building className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Aucun COOPER&apos;S trouvé</p>
            <Button className="mt-4" onClick={() => setShowCreateCooperModal(true)}><UserPlus className="h-4 w-4 mr-2" />Créer un COOPER&apos;S</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
